/**
 * RoomOpener (PPL-32 → redesigned PPL-67 / C7) — the ceremonial joint
 * synthesis playback. Shown to both participants once the room enters `joint`
 * state (synthesis ready, room assembled).
 *
 * Per the C7 canvas: ceremonial, low chrome, large italic opening line,
 * centered air. Drops the "glass card" framing in favour of three plain
 * sections separated by hairlines — "from each of you", "something you both
 * said, in different words", and a closing both-sides-ready footer.
 *
 * Wiring preserved end-to-end:
 *  - `subscribeToRoom` drives the live mediation doc.
 *  - `setRoomReady` writes my "i'm ready" via setMediationReadyEurW.
 *  - The room is opened automatically (navigate to `/rooms/:id/room`) when
 *    both `mediation.ready[uid]` flags are true.
 *  - `retrySynthesis` is available if synthesis failed.
 */

import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeToRoom,
  setRoomReady,
  retrySynthesis,
  getMediationCounterpart,
} from '../services/roomService';
import type { Room, SynthesisBlockItem } from '../types/room';
import SEO from '../components/SEO';

// ─── Side palette ────────────────────────────────────────────

type SideKey = 'inviter' | 'invitee';

interface SidePerson {
  short: string;
  hue: number;
  soft: string;
  deep: string;
}

const PALETTES: Record<SideKey, SidePerson> = {
  inviter: { short: 'them', hue: 325, soft: '#f0abfc', deep: '#d946ef' },
  invitee: { short: 'them', hue: 258, soft: '#a5b4fc', deep: '#818cf8' },
};

// ─── Atoms ───────────────────────────────────────────────────

function DualAurora() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-22%',
          left: '-12%',
          width: '70%',
          height: '70%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fb923c 0%, transparent 65%)',
          filter: 'blur(85px)',
          opacity: 0.5,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '8%',
          right: '-14%',
          width: '68%',
          height: '72%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #d946ef 0%, transparent 65%)',
          filter: 'blur(90px)',
          opacity: 0.42,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30%',
          left: '20%',
          width: '64%',
          height: '70%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #6366f1 0%, transparent 65%)',
          filter: 'blur(95px)',
          opacity: 0.42,
        }}
      />
    </div>
  );
}

function CeremonyHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: 'rgba(232,220,250,0.5)',
        textAlign: 'center',
        marginBottom: 24,
      }}
    >
      {children}
    </div>
  );
}

function CeremonyDivider() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '46px 0' }}>
      <div style={{ width: 72, height: 1, background: 'rgba(255,255,255,0.14)' }} />
    </div>
  );
}

function Avatar({ name, hue, size = 22 }: { name: string; hue: number; size?: number }) {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(circle at 30% 30%, hsla(${hue},80%,72%,0.55), hsla(${hue},75%,40%,0.85))`,
        border: `1px solid hsla(${hue},75%,60%,0.4)`,
        fontWeight: 700,
        fontSize: size * 0.4,
        color: '#fff',
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </span>
  );
}

function SourceLine({
  person,
  text,
}: {
  person: SidePerson & { displayName: string };
  text: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        maxWidth: 720,
        margin: '0 auto',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 2,
          alignSelf: 'stretch',
          borderRadius: 2,
          background: `linear-gradient(180deg, ${person.deep}, ${person.deep}44)`,
        }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, flexWrap: 'wrap' }}>
          <Avatar name={person.displayName} hue={person.hue} size={22} />
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: person.soft,
            }}
          >
            {person.short}
          </span>
          <span
            style={{
              fontSize: 10,
              color: 'rgba(232,220,250,0.35)',
              letterSpacing: '0.04em',
              fontStyle: 'italic',
            }}
          >
            from {person.short}&apos;s prep, with permission
          </span>
        </div>
        <div
          style={{
            fontSize: 16,
            color: '#fff',
            lineHeight: 1.55,
            letterSpacing: '-0.005em',
            whiteSpace: 'pre-wrap',
          }}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function ReadyChip({
  person,
  ready,
  label,
}: {
  person: SidePerson & { displayName: string };
  ready: boolean;
  label: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        padding: '11px 14px',
        borderRadius: 12,
        background: ready ? `${person.deep}1f` : 'rgba(255,255,255,0.04)',
        border: ready ? `1px solid ${person.deep}55` : '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
      }}
    >
      <Avatar name={person.displayName} hue={person.hue} size={22} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
      </div>
      {ready && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12.5l4.5 4.5L19 7"
            stroke={person.soft}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

// FootLink removed with the opener's backward affordances (PPL-161).

// ─── Page ────────────────────────────────────────────────────

export default function RoomOpener() {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const [mediation, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const [myName, setMyName] = useState('you');
  const [otherName, setOtherName] = useState('them');

  const navigatedRef = useRef(false);

  // Subscribe to room
  useEffect(() => {
    if (!mediationId) {
      setError('missing mediation id');
      setLoading(false);
      return;
    }
    const unsub = subscribeToRoom(
      mediationId,
      (data) => {
        if (!data) {
          setRoom(null);
          setError('room not found');
          setLoading(false);
          return;
        }
        setRoom(data);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err?.message ?? 'could not load room');
        setLoading(false);
      },
    );
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, [mediationId]);

  // Fetch both display names via the mediation-scoped resolver — it returns
  // firstName for both sides in one auth'd round-trip, unlike the handle-only
  // getPublicUserProfileEurW.
  useEffect(() => {
    if (!mediationId) return;
    let cancelled = false;
    (async () => {
      try {
        const { self, counterpart } = await getMediationCounterpart(mediationId);
        if (cancelled) return;
        if (self.firstName) setMyName(self.firstName.toLowerCase());
        if (counterpart.firstName) setOtherName(counterpart.firstName.toLowerCase());
      } catch {
        /* leave fallbacks */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mediationId]);

  // Terminal guard: never hold a dead room on the synthesis spinner. The
  // pre-synthesis state renders an indefinite "f8 is preparing the synthesis…";
  // if the room ended underneath us (the other side stepped out) nothing is
  // being prepared and that wait never resolves. Declared BEFORE the both-ready
  // effect so an ended room wins the single navigation.
  useEffect(() => {
    if (!mediation || navigatedRef.current) return;
    if (mediation.state === 'ended') {
      navigatedRef.current = true;
      navigate(`/rooms/${mediationId}/summary`, { replace: true });
    }
  }, [mediation, mediationId, navigate]);

  // Auto-navigate when both ready
  useEffect(() => {
    if (!mediation || navigatedRef.current) return;
    const { ready, participants } = mediation;
    const bothReady =
      participants.length === 2 && participants.every((p) => !!ready?.[p]);
    if (bothReady) {
      navigatedRef.current = true;
      navigate(`/rooms/${mediationId}/room`, { replace: true });
    }
  }, [mediation, mediationId, navigate]);

  // Mark ready
  const handleReady = useCallback(async () => {
    if (!mediationId || marking) return;
    setMarking(true);
    try {
      await setRoomReady(mediationId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'something went wrong — please try again';
      setError(msg);
    } finally {
      setMarking(false);
    }
  }, [mediationId, marking]);

  // Retry synthesis after failure
  const handleRetrySynthesis = useCallback(async () => {
    if (!mediationId || retrying) return;
    setRetrying(true);
    setError(null);
    try {
      await retrySynthesis(mediationId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'retry failed — please try again shortly';
      setError(msg);
    } finally {
      setRetrying(false);
    }
  }, [mediationId, retrying]);

  // ── Derived ────────────────────────────────────────────────
  const isInviter = !!mediation && !!uid && mediation.inviterUid === uid;
  const sideKey: SideKey = isInviter ? 'inviter' : 'invitee';
  const otherSideKey: SideKey = isInviter ? 'invitee' : 'inviter';
  const otherUid = mediation?.participants.find((p) => p !== uid) ?? null;

  const myReady = !!(mediation && uid && mediation.ready?.[uid]);
  const theirReady = !!(mediation && otherUid && mediation.ready?.[otherUid]);

  const myPerson = useMemo<SidePerson & { displayName: string }>(
    () => ({ ...PALETTES[sideKey], short: myName, displayName: myName }),
    [sideKey, myName],
  );
  const otherPerson = useMemo<SidePerson & { displayName: string }>(
    () => ({ ...PALETTES[otherSideKey], short: otherName, displayName: otherName }),
    [otherSideKey, otherName],
  );

  const blocks: SynthesisBlockItem[] =
    (mediation?.synthesis?.blocks ?? []) as SynthesisBlockItem[];
  const sharedOpener = blocks.find((b) => !b.fromUid)?.text ?? null;
  const myBlocks = blocks.filter((b) => b.fromUid === uid);
  const otherBlocks = blocks.filter((b) => !!b.fromUid && b.fromUid !== uid);

  const synthesisReady = mediation?.synthesisStatus === 'ready' && !!sharedOpener;

  // ── Short-circuits ─────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          height: '100%',
          minHeight: 0,
          background: '#0a0510',
          color: 'rgba(232,220,250,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <SEO title="The opening · f8" />
        <span>loading…</span>
      </div>
    );
  }

  if (error || !mediation) {
    return (
      <div
        style={{
          height: '100%',
          minHeight: 0,
          background: '#0a0510',
          color: 'rgba(232,220,250,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <SEO title="The opening · f8" />
        <span>{error ?? 'room not found'}</span>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────
  const containerStyle: CSSProperties = {
    position: 'relative',
    // Fill the shell's <main> and let the body scroll internally, rather than a
    // full-viewport min-height that overflows its slot — otherwise the long
    // synthesis is clipped with no way to scroll to the bottom / the CTA.
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#0a0510',
    color: '#fff',
    fontFamily: "'Inter', system-ui, sans-serif",
    overflow: 'hidden',
  };

  return (
    <div style={containerStyle}>
      <SEO title="The opening · f8" />
      <DualAurora />

      {/* top bar */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px 12px',
        }}
      >
        {/* Forward-only: no back button — you can't return to prep once past it
            (PPL-161). Spacer (matches the right spacer) keeps the eyebrow centered. */}
        <div style={{ width: 40 }} />
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(232,220,250,0.4)',
          }}
        >
          opening · session 1
        </div>
        <div style={{ width: 40 }} />
      </div>

      {/* body — the scroll surface; top bar stays fixed above it */}
      <main
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          width: '100%',
          maxWidth: 820,
          margin: '0 auto',
          padding: '28px 28px 80px',
        }}
      >
        {/* ceremonial opener — eyebrow + horizontal hair */}
        <div style={{ textAlign: 'center', margin: '24px 0 26px' }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(232,220,250,0.55)',
            }}
          >
            f8 · holding the opening
          </div>
          <div
            style={{
              width: 32,
              height: 1,
              background: 'rgba(255,255,255,0.2)',
              margin: '14px auto 0',
            }}
          />
        </div>

        {/* Synthesis status — failed / pending / ready */}
        {mediation.synthesisStatus === 'failed' ? (
          <div
            style={{
              padding: '20px 24px',
              borderRadius: 16,
              background: 'rgba(239,68,68,0.06)',
              border: '1px solid rgba(239,68,68,0.22)',
              maxWidth: 560,
              margin: '0 auto 32px',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#fff' }}>
              f8 had trouble preparing the synthesis.
            </p>
            {mediation.synthesisError && (
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(232,220,250,0.65)' }}>
                {mediation.synthesisError}
              </p>
            )}
            <button
              type="button"
              onClick={handleRetrySynthesis}
              disabled={retrying}
              style={{
                appearance: 'none',
                border: '1px solid rgba(255,255,255,0.14)',
                cursor: retrying ? 'progress' : 'pointer',
                padding: '10px 20px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                opacity: retrying ? 0.6 : 1,
              }}
            >
              {retrying ? 'trying again…' : 'try again'}
            </button>
          </div>
        ) : !synthesisReady ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: 'rgba(232,220,250,0.65)',
              fontSize: 14,
            }}
          >
            f8 is preparing the synthesis…
          </div>
        ) : (
          <>
            {/* large italic opening line — the shared synthesis */}
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: '#fff',
                letterSpacing: '-0.02em',
                lineHeight: 1.4,
                textAlign: 'center',
                fontStyle: 'italic',
                marginBottom: 60,
                maxWidth: 720,
                margin: '0 auto 60px',
              }}
            >
              {sharedOpener}
            </div>

            {/* from each of you */}
            {(myBlocks.length > 0 || otherBlocks.length > 0) && (
              <>
                <CeremonyHeader>from each of you</CeremonyHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 22, marginBottom: 8 }}>
                  {otherBlocks.map((b, i) => (
                    <SourceLine
                      key={`other-${i}`}
                      person={otherPerson}
                      text={b.text}
                    />
                  ))}
                  {myBlocks.map((b, i) => (
                    <SourceLine
                      key={`me-${i}`}
                      person={myPerson}
                      text={b.text}
                    />
                  ))}
                </div>
                <CeremonyDivider />
              </>
            )}

            {/* closing */}
            <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 8px' }}>
              <div
                style={{
                  fontSize: 16,
                  color: 'rgba(255,255,255,0.95)',
                  lineHeight: 1.6,
                  letterSpacing: '-0.005em',
                }}
              >
                that&apos;s all i&apos;m bringing in. everything else stays in your solo rooms.
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: 'rgba(232,220,250,0.55)',
                  lineHeight: 1.55,
                  marginTop: 10,
                }}
              >
                when you&apos;re both ready, we can start where you each are.
              </div>
            </div>

            {/* both-sided ready row */}
            <div
              style={{
                marginTop: 38,
                display: 'flex',
                gap: 10,
                maxWidth: 580,
                margin: '38px auto 0',
              }}
            >
              <ReadyChip
                person={otherPerson}
                ready={theirReady}
                label={theirReady ? `${otherName} is ready` : `waiting on ${otherName}`}
              />
              <ReadyChip
                person={myPerson}
                ready={myReady}
                label={myReady ? "you're ready" : 'waiting on you'}
              />
            </div>

            {/* enter CTA */}
            <div style={{ marginTop: 16, textAlign: 'center', maxWidth: 580, margin: '16px auto 8px' }}>
              {!myReady ? (
                <button
                  type="button"
                  onClick={handleReady}
                  disabled={marking || !synthesisReady}
                  style={{
                    appearance: 'none',
                    border: 0,
                    cursor: marking ? 'progress' : 'pointer',
                    width: '100%',
                    padding: '15px 22px',
                    borderRadius: 999,
                    background: 'linear-gradient(135deg, #d946ef 0%, #a855f7 100%)',
                    color: '#fff',
                    fontSize: 14.5,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    boxShadow: '0 8px 24px rgba(217,70,239,0.32)',
                    opacity: marking || !synthesisReady ? 0.6 : 1,
                  }}
                >
                  {marking ? 'marking ready…' : "i'm ready · start the room"}
                </button>
              ) : (
                <div
                  style={{
                    padding: '14px',
                    fontSize: 13.5,
                    color: 'rgba(232,220,250,0.7)',
                  }}
                >
                  you&apos;re ready. {theirReady ? 'opening the room…' : `waiting on ${otherName}.`}
                </div>
              )}

              {/* Forward-only: no "ask f8 first → prep" or "step back" affordances —
                  the opener only moves forward into the room (PPL-161). */}
            </div>

            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 18,
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  fontSize: 13,
                  color: '#fca5a5',
                  textAlign: 'center',
                  maxWidth: 580,
                  margin: '18px auto 0',
                }}
              >
                {error}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
